import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Element used to determine when the sticky shell should activate.
   * Falls back to the header itself when no sentinel is present.
   * @type {HTMLElement | null}
   */
  #stickyTrigger = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  #getStickyShell() {
    const stickyShell = this.querySelector(':scope > .header__sticky-shell');
    return stickyShell instanceof HTMLElement ? stickyShell : null;
  }

  #ensureSplitStickySetup = () => {
    if (!this.hasAttribute('sticky') || this.hasAttribute('transparent')) return;

    const announcement = this.querySelector(':scope > .header__announcement');
    const stickyShell = this.#getStickyShell();

    if (!(announcement instanceof HTMLElement) || !(stickyShell instanceof HTMLElement)) return;

    this.dataset.stickyShellMode = 'split';

    const stickyTrigger = this.querySelector(':scope > .header__sticky-sentinel');
    if (stickyTrigger instanceof HTMLElement) return;

    const sentinel = document.createElement('div');
    sentinel.className = 'header__sticky-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    stickyShell.before(sentinel);
  };

  #resetSplitStickyShell = () => {
    const stickyShell = this.#getStickyShell();
    if (!(stickyShell instanceof HTMLElement)) return;

    this.style.removeProperty('padding-bottom');
    stickyShell.style.removeProperty('position');
    stickyShell.style.removeProperty('top');
    stickyShell.style.removeProperty('left');
    stickyShell.style.removeProperty('right');
    stickyShell.style.removeProperty('width');
    stickyShell.style.removeProperty('z-index');
    stickyShell.style.removeProperty('background-color');
    stickyShell.style.removeProperty('background-image');
    stickyShell.style.removeProperty('box-shadow');
    stickyShell.style.removeProperty('opacity');
  };

  #syncSplitStickyShell = () => {
    if (this.dataset.stickyShellMode !== 'split') {
      this.#resetSplitStickyShell();
      return;
    }

    const stickyShell = this.#getStickyShell();
    if (!(stickyShell instanceof HTMLElement)) return;

    const stickyMode = this.getAttribute('sticky');
    const isActive =
      this.dataset.stickyState === 'active' ||
      (stickyMode === 'scroll-up' && this.dataset.stickyState === 'idle');

    if (!isActive) {
      this.#resetSplitStickyShell();
      return;
    }

    const { left, width } = this.getBoundingClientRect();
    const stickyShellHeight = Math.round(stickyShell.offsetHeight);

    this.style.paddingBottom = `${stickyShellHeight}px`;
    stickyShell.style.position = 'fixed';
    stickyShell.style.top = '0px';
    stickyShell.style.left = `${Math.round(left)}px`;
    stickyShell.style.right = 'auto';
    stickyShell.style.width = `${Math.round(width)}px`;
    stickyShell.style.zIndex = 'var(--layer-sticky)';
    stickyShell.style.opacity = this.dataset.stickyState === 'idle' ? '0' : '1';

    if (!this.hasAttribute('transparent')) {
      stickyShell.style.backgroundColor = '#ee7e9f';
      stickyShell.style.backgroundImage = 'var(--site-header-surface)';
      stickyShell.style.boxShadow = 'var(--site-header-shadow)';
    }
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    const stickyShell = this.querySelector('.header__sticky-shell');
    if (stickyShell instanceof HTMLElement) {
      this.style.setProperty('--sticky-shell-height', `${Math.round(stickyShell.offsetHeight)}px`);
    }

    this.#syncSplitStickyShell();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#stickyTrigger =
      this.querySelector(':scope > .header__sticky-sentinel') instanceof HTMLElement
        ? this.querySelector(':scope > .header__sticky-sentinel')
        : this;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        const isInNaturalPosition = scrollTop <= 0;

        this.dataset.stickyState = isInNaturalPosition ? 'inactive' : 'active';
        this.#syncSplitStickyShell();
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this.#stickyTrigger);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Supports older header-group configs where the announcement renders
   * as a sibling section before the header section.
   */
  #adoptLegacyAnnouncementBar = () => {
    const headerSection = this.closest('.header-section');
    if (!(headerSection instanceof HTMLElement)) return;

    const legacyAnnouncementSection = headerSection.previousElementSibling;
    if (!(legacyAnnouncementSection instanceof HTMLElement)) return;

    const legacyAnnouncementBar = legacyAnnouncementSection.querySelector(':scope > .announcement-bar');
    if (!(legacyAnnouncementBar instanceof HTMLElement)) return;

    if (this.querySelector('.header__announcement')) {
      legacyAnnouncementSection.remove();
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'header__announcement';

    legacyAnnouncementBar.classList.add('header__announcement-inner');
    wrapper.append(legacyAnnouncementBar);
    this.prepend(wrapper);

    legacyAnnouncementSection.remove();
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #refreshStickyObserver = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#stickyTrigger = null;
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

    requestAnimationFrame(() => {
      this.#ensureSplitStickySetup();
      this.#observeStickyPosition(stickyMode === 'always');
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = stickyMode === 'always' ? scrollTop <= 0 : (this.#stickyTrigger ?? this).getBoundingClientRect().top >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.dataset.stickyState = isAtTop ? 'inactive' : 'active';
      this.#syncSplitStickyShell();

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.#syncSplitStickyShell();
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#syncSplitStickyShell();
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
      this.#syncSplitStickyShell();
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      this.#syncSplitStickyShell();
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#adoptLegacyAnnouncementBar();
    this.#ensureSplitStickySetup();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.addEventListener('fly-to-cart:complete', this.#refreshStickyObserver);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#observeStickyPosition(stickyMode === 'always');
      this.#syncSplitStickyShell();

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#stickyTrigger = null;
    this.#resetSplitStickyShell();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('fly-to-cart:complete', this.#refreshStickyObserver);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
